/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import typography from '@amblique/base/app/theme/foundations/typography';

/* eslint-disable sort-keys */
export default {
  ...typography,

  lineHeights: {
    ...typography.lineHeights,

    none: 1,
    shorter: 1.2,
    short: 1.3,
    base: 1.4,
    tall: 1.6,
    taller: 2,
  },

  letterSpacings: {
    ...typography.letterSpacings,

    slightlyTight: '-0.0275rem',
    headingTight: '-0.0175rem',
    headingTighter: '-0.025rem',
    labelTight: '-0.01375rem',
    eyebrow: '0.075rem',
    eyebrowLg: '0.0875rem',
  },

  fontSizes: {
    ...typography.fontSizes,

    /**
     * @override default Chakra font sizes to King Living font sizes
     */
    '3xs': '0.45rem',
    '2xs': '0.625rem',
    'xs': '0.75rem',
    'sm': '0.875rem',
    'md': '1rem',
    'lg': '1.125rem',
    'xl': '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
    '4xl': '2.25rem',
    '5xl': '3rem',
    '6xl': '3.75rem',
    '7xl': '4.5rem',
    '8xl': '6rem',
    '9xl': '8rem',

    /**
     * @deprecated Use explicit font sizes instead
     */
    '2.75xs': '0.6875rem',
    '1xs': '0.8125rem',
    '1.5xs': '0.8125rem',
    '2sm': '0.9375rem',
    '1.5xl': '1.375rem',
    '2.5xl': '1.625rem',
    '2.75xl': '1.75rem',
    '3.25xl': '2.0125rem',
    '3.5xl': '2.125rem',
    '4.5xl': '2.375rem',
    '4.6xl': '2.875rem',
    '4.75xl': '2.5rem',
  },

  fonts: {
    heading: 'aktiv-grotesk, "sans-serif"',
    body: 'aktiv-grotesk, "sans-serif"',
    mono: 'aktiv-grotesk-thin, "sans-serif"',
  },

  // Typography preset system (textStyles)
  // Use via textStyle prop: <Text textStyle="displayXL">...</Text>
  // Responsive font sizes: base (mobile) and lg (desktop)
  // Chakra UI breakpoints: base: 0em, lg: 62em
  textStyles: {
    // Display XL: Mobile 64px → Desktop 96px
    displayXL: {
      fontSize: {base: '4rem', lg: '6rem'},
      fontWeight: 400,
      lineHeight: 1,
      fontFamily: 'heading',
    },
    // Display L: Mobile 48px → Desktop 72px
    displayL: {
      fontSize: {base: '3rem', lg: '4.5rem'},
      fontWeight: 400,
      lineHeight: 1,
      fontFamily: 'heading',
    },
    // Display M: Mobile 40px → Desktop 60px
    displayM: {
      fontSize: {base: '2.5rem', lg: '3.75rem'},
      fontWeight: 500,
      lineHeight: 1.2,
      fontFamily: 'heading',
    },
    // Heading XL: Mobile 32px → Desktop 40px
    headingXL: {
      fontSize: {base: '2rem', lg: '2.5rem'},
      fontWeight: 500,
      lineHeight: 1.2,
      fontFamily: 'heading',
    },
    // Heading L: Mobile 24px → Desktop 32px
    headingL: {
      fontSize: {base: '1.5rem', lg: '2rem'},
      fontWeight: 500,
      lineHeight: {base: 1.5, lg: 1.4},
      fontFamily: 'heading',
    },
    // Label XL: Mobile 20px → Desktop 24px
    labelXL: {
      fontSize: {base: '1.25rem', lg: '1.5rem'},
      fontWeight: 500,
      lineHeight: {base: 1.5, lg: 1.4},
      fontFamily: 'body',
    },
    // Body XL: Mobile 20px → Desktop 24px
    bodyXL: {
      fontSize: {base: '1.25rem', lg: '1.5rem'},
      fontWeight: 400,
      lineHeight: 1.4,
      fontFamily: 'body',
    },
    // Label L: Mobile 16px → Desktop 18px
    labelL: {
      fontSize: {base: '1rem', lg: '1.125rem'},
      fontWeight: 500,
      lineHeight: 1.6,
      fontFamily: 'body',
    },
    // Label Med: Figma section title (e.g. "In this collection") — 32px / 500 / 120% / Aktiv Grotesk
    labelMed: {
      fontFamily: 'heading',
      fontSize: {base: '1.75rem', md: '2rem'},
      fontStyle: 'normal',
      fontWeight: 500,
      lineHeight: 1.2,
      fontVariantNumeric: 'lining-nums proportional-nums',
    },
    // Section title: Product tiles / Einstein blocks — 28px mobile, 40px desktop
    sectionTitle: {
      fontFamily: 'heading',
      fontSize: {base: '1.75rem', lg: '2.5rem'},
      fontStyle: 'normal',
      fontWeight: 500,
      lineHeight: 1.2,
      letterSpacing: {base: '-0.28px', lg: '-0.4px'},
      fontVariantNumeric: 'lining-nums proportional-nums',
    },
    // Body L: Mobile 16px → Desktop 18px
    bodyL: {
      fontSize: {base: '1rem', lg: '1.125rem'},
      fontWeight: 400,
      lineHeight: 1.6,
      fontFamily: 'body',
    },
    // Label default: Mobile 14px → Desktop 16px
    labelDefault: {
      fontSize: {base: '0.875rem', lg: '1rem'},
      fontWeight: 600,
      lineHeight: 1.4,
      fontFamily: 'body',
    },
    // Body default: Mobile 14px → Desktop 16px
    bodyDefault: {
      fontSize: {base: '0.875rem', lg: '1rem'},
      fontWeight: 400,
      lineHeight: 1.4,
      fontFamily: 'body',
    },
    // Label S: Mobile 13px → Desktop 12px
    labelS: {
      fontSize: {base: '0.8125rem', lg: '0.75rem'},
      fontWeight: 600,
      lineHeight: 1.2,
      fontFamily: 'body',
    },
    // Body S: Mobile 13px → Desktop 12px
    bodyS: {
      fontSize: {base: '0.8125rem', lg: '0.75rem'},
      fontWeight: 400,
      lineHeight: 1.2,
      fontFamily: 'body',
    },
    // Eyebrow 12: 12px (same for mobile and desktop)
    eyebrow12: {
      fontSize: '0.75rem',
      fontWeight: 600,
      lineHeight: 1.2,
      fontFamily: 'body',
    },
    // Eyebrow 10: 10px (same for mobile and desktop)
    eyebrow10: {
      fontSize: '0.625rem',
      fontWeight: 600,
      lineHeight: 1.2,
      fontFamily: 'body',
    },
  },
};
